import { useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DriverDashboard from './DriverDashboard';
import OfficeDashboard from './OfficeDashboard';
import DispatcherDashboard from './DispatcherDashboard';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useProxyRole, ROLE_LABELS, ROLE_COLORS } from '../contexts/ProxyRoleContext';
import { fetchOrders, isPastDue, daysBetween } from '../services/orderService';
import { queryKeys } from '../lib/queryClient';
import {
  Text,
  Badge,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  VehicleTruck24Regular,
  ShoppingBag24Regular,
  People24Regular,
  DocumentText24Regular,
  ClipboardTaskListLtr24Regular,
  Money24Regular,
  Mail24Regular,
  Wrench24Regular,
  Warning24Regular,
  ArrowRight16Regular,
  DataTrendingRegular,
  CalendarLtr24Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    overflowX: 'hidden',
    marginLeft: '-1px',
  },

  // ── Hero ──────────────────────────────────────────────────────────
  hero: {
    background: 'linear-gradient(135deg, #4F8EF7 0%, #7EC8E3 60%, #C8E6F5 100%)',
    ...shorthands.padding('56px', '40px'),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '40px',
    position: 'relative',
    flexShrink: '0',
    ...shorthands.overflow('hidden'),
    '@media (max-width: 768px)': {
      ...shorthands.padding('32px', '16px'),
      gap: '24px',
    },
  },
  heroGreeting: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#ffffff',
    textShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  heroQuickRow: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    gap: '12px',
    zIndex: '1',
    // Four cards in one row overflow the hero on phones (it clips via
    // overflow:hidden). Wrap into a 2x2 grid that fills the hero width.
    '@media (max-width: 768px)': {
      gridAutoFlow: 'row',
      gridTemplateColumns: '1fr 1fr',
      width: '100%',
    },
  },
  heroQuickCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ...shorthands.padding('14px', '20px'),
    ...shorthands.borderRadius('12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    cursor: 'pointer',
    ...shorthands.transition('all', '0.15s', 'ease'),
    '&:hover': {
      boxShadow: tokens.shadow16,
    },
    '&:hover > :first-child': {
      transform: 'scale(1.15)',
    },
  },
  heroQuickIconCircle: {
    width: '44px',
    height: '44px',
    ...shorthands.borderRadius('50%'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease',
  },
  heroQuickLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },

  // ── Dashboard body ─────────────────────────────────────────────────
  dashboard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    ...shorthands.padding('24px', '28px'),
    '@media (max-width: 768px)': {
      ...shorthands.padding('16px', '12px'),
      gap: '16px',
    },
  },

  // ── Stat cards ─────────────────────────────────────────────────────
  statsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    // 4-up KPI stats are tight at 375px — stack them so each card
    // gets full width and the value/trend numbers remain readable.
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  statCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('20px'),
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    ...shorthands.transition('box-shadow', '0.15s', 'ease'),
    '&:hover': {
      boxShadow: tokens.shadow8,
    },
  },
  statIconWrap: {
    width: '48px',
    height: '48px',
    ...shorthands.borderRadius('10px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
  },
  statBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    lineHeight: '1',
    color: tokens.colorNeutralForeground1,
  },
  statLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontWeight: '500',
  },
  statTrend: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '12px',
    fontWeight: '600',
  },

  // ── Main row ───────────────────────────────────────────────────────
  mainRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.6fr',
    gap: '16px',
    alignItems: 'start',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  leftCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  // ── Panel ──────────────────────────────────────────────────────────
  panel: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius('4px'),
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('16px', '20px', '12px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  panelTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },

  // ── Revenue sparkline ──────────────────────────────────────────────
  sparklineBody: {
    ...shorthands.padding('16px', '20px', '12px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sparklineBars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '6px',
    height: '56px',
  },
  sparklineCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    flex: '1',
  },
  sparklineBar: {
    width: '100%',
    ...shorthands.borderRadius('3px', '3px', '0', '0'),
    minHeight: '4px',
  },
  sparklineDay: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    fontWeight: '500',
  },

  // ── Alert list ─────────────────────────────────────────────────────
  alertList: {
    display: 'flex',
    flexDirection: 'column',
  },
  alertItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('14px', '20px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  alertIconWrap: {
    width: '36px',
    height: '36px',
    ...shorthands.borderRadius('50%'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
  },
  alertText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1',
  },
  alertTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  alertDesc: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },

  // ── Recent orders ──────────────────────────────────────────────────
  recentList: {
    display: 'flex',
    flexDirection: 'column',
  },
  recentItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '20px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    gap: '12px',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  recentLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1',
    minWidth: '0',
  },
  recentCustomer: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  recentNumber: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  recentRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: '0',
  },
  recentTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
});

function getGreeting(name: string): string {
  const hour = new Date().getHours();
  const salutation = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = name.split(' ')[0];
  return `${salutation}, ${firstName}`;
}

function getWeekStart(): Date {
  const d = new Date();
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function getLast7Days(): { day: string; date: Date }[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - 6 + i);
    d.setHours(0, 0, 0, 0);
    return { day: d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3), date: d };
  });
}

// Static alert for items without a DB source yet
const STATIC_ALERTS = [
  {
    iconColor: '#C27820', iconBg: '#FFF4DE',
    icon: <CalendarLtr24Regular style={{ color: '#C27820' }} />,
    title: 'Equipment rental contract expiring',
    desc: 'Expires in 7 days — review required',
    path: '/documents',
  },
];

function relativeTime(dateStr: string): string {
  const hours = Math.floor((Date.now() - new Date(dateStr).getTime()) / 3_600_000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
}

export default function Home() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const { proxyRole } = useProxyRole();
  const activeRole = proxyRole ?? 'owner';
  const { data: orders = [] } = useQuery({
    queryKey: queryKeys.orders.all,
    queryFn: fetchOrders,
    staleTime: 5 * 60 * 1000,
  });

  const openOrders = useMemo(
    () => orders.filter(o => !['archived', 'canceled', 'stopped'].includes(o.booqableStatus)).length,
    [orders]
  );

  const outstandingAR = useMemo(
    () => orders
      .filter(o => o.booqableStatus !== 'canceled' && o.price > o.paidAmount)
      .reduce((sum, o) => sum + (o.price - o.paidAmount), 0),
    [orders]
  );


  const overdueAlerts = useMemo(() =>
    orders
      .filter(o => isPastDue(o.endDate) && o.price > o.paidAmount && !['canceled', 'archived'].includes(o.booqableStatus))
      .sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime())
      .slice(0, 2)
      .map(o => {
        const days = daysBetween(o.endDate);
        const critical = days > 30;
        return {
          iconColor: critical ? '#E05252' : '#C27820',
          iconBg: critical ? '#FDEAEA' : '#FFF4DE',
          icon: <Warning24Regular style={{ color: critical ? '#E05252' : '#C27820' }} />,
          title: `${o.customerName} — ${days} days overdue`,
          desc: `$${(o.price - o.paidAmount).toFixed(2)} outstanding`,
          path: '/customers',
        };
      }),
    [orders]
  );

  const revenueThisWeek = useMemo(() => {
    const weekStart = getWeekStart();
    const now = new Date();
    return orders
      .filter(o => !['canceled', 'archived'].includes(o.booqableStatus) && o.startDate)
      .filter(o => { const d = new Date(o.startDate); return d >= weekStart && d <= now; })
      .reduce((sum, o) => sum + o.price, 0);
  }, [orders]);

  const revenueByDay = useMemo(() => {
    const days = getLast7Days();
    return days.map(({ day, date }) => {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      const value = orders
        .filter(o => o.booqableStatus !== 'canceled' && o.startDate)
        .filter(o => { const d = new Date(o.startDate); return d >= date && d < nextDay; })
        .reduce((sum, o) => sum + o.price, 0);
      return { day, value };
    });
  }, [orders]);

  const pendingInvoices = useMemo(() =>
    orders.filter(o =>
      !['canceled', 'archived'].includes(o.booqableStatus) &&
      // Mirror vocabulary (#3): an unpaid invoice is payment_due or
      // partially_paid (replaces the legacy pending/overdue values).
      (o.paymentStatus === 'payment_due' || o.paymentStatus === 'partially_paid')
    ).length,
    [orders]
  );

  const alerts = [...overdueAlerts, ...STATIC_ALERTS];

  const stats = [
    {
      label: 'Revenue This Week',
      value: `$${revenueThisWeek.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      trend: null,
      color: '#4F8EF7', bg: '#EAF2FF',
      icon: <Money24Regular style={{ color: '#4F8EF7' }} />,
      path: '/financial',
    },
    {
      label: 'Outstanding A/R',
      value: `$${outstandingAR.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      trend: null,
      color: '#C27820', bg: '#FFF4DE',
      icon: <DocumentText24Regular style={{ color: '#C27820' }} />,
      path: '/financial',
    },
    {
      label: 'Open Orders',
      value: String(openOrders),
      trend: null,
      color: '#9B6CF7', bg: '#F3EEFF',
      icon: <ShoppingBag24Regular style={{ color: '#9B6CF7' }} />,
      path: '/orders',
    },
    {
      label: 'Pending Invoices',
      value: String(pendingInvoices),
      trend: null,
      color: '#F7834F', bg: '#FFF0E8',
      icon: <ClipboardTaskListLtr24Regular style={{ color: '#F7834F' }} />,
      path: '/financial',
    },
  ];

  const quickActions = [
    { icon: <DataTrendingRegular   style={{ color: '#fff' }} />, label: 'Reports',          color: '#4F8EF7', onClick: () => navigate('/reports') },
    { icon: <Money24Regular        style={{ color: '#fff' }} />, label: 'Overdue Accounts', color: '#C4314B', onClick: () => navigate('/financial') },
    { icon: <ShoppingBag24Regular  style={{ color: '#fff' }} />, label: 'Active Orders',    color: '#9B6CF7', onClick: () => navigate('/orders') },
    { icon: <Mail24Regular         style={{ color: '#fff' }} />, label: 'Messages',         color: '#3DBE7A', onClick: () => navigate('/Messaging') },
  ];

  const maxRevenue = Math.max(...revenueByDay.map(d => d.value), 1);

  if (user && !user.owner) return <Navigate to="/customers" replace />;

  if (activeRole === 'driver')     return <DriverDashboard />;
  if (activeRole === 'office')     return <OfficeDashboard />;
  if (activeRole === 'dispatcher') return <DispatcherDashboard />;

  // Other non-owner/non-admin roles — placeholder until built
  if (activeRole !== 'owner' && activeRole !== 'admin') {
    return (
      <div className={styles.page}>
        <div className={styles.hero}>
          <Text className={styles.heroGreeting}>{user ? getGreeting(user.name) : 'Welcome'}</Text>
          <div className={styles.heroQuickRow}>
            {quickActions.map((item, i) => (
              <div key={i} className={styles.heroQuickCard} onClick={item.onClick}>
                <div className={styles.heroQuickIconCircle} style={{ backgroundColor: item.color }}>
                  {item.icon}
                </div>
                <Text className={styles.heroQuickLabel}>{item.label}</Text>
              </div>
            ))}
          </div>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          gap: '12px',
          color: ROLE_COLORS[activeRole],
          padding: '48px',
        }}>
          <Text style={{ fontSize: '18px', fontWeight: '600', color: ROLE_COLORS[activeRole] }}>
            {ROLE_LABELS[activeRole]} dashboard
          </Text>
          <Text style={{ fontSize: '14px', color: '#888' }}>
            This view is not built yet. Use the persona to define what goes here.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Hero */}
      <div className={styles.hero}>
        <Text className={styles.heroGreeting}>{user ? getGreeting(user.name) : 'Welcome'}</Text>
        <div className={styles.heroQuickRow}>
          {quickActions.map((item, i) => (
            <div key={i} className={styles.heroQuickCard} onClick={item.onClick}>
              <div className={styles.heroQuickIconCircle} style={{ backgroundColor: item.color }}>
                {item.icon}
              </div>
              <Text className={styles.heroQuickLabel}>{item.label}</Text>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.dashboard}>
        <div className={styles.mainRow}>
          {/* Left column: stat cards + needs attention */}
          <div className={styles.leftCol}>
            <div className={styles.statsRow}>
              {stats.map((s, i) => (
                <div key={i} className={styles.statCard} onClick={() => navigate(s.path)}>
                  <div className={styles.statIconWrap} style={{ backgroundColor: s.bg }}>
                    {s.icon}
                  </div>
                  <div className={styles.statBody}>
                    <Text className={styles.statValue}>{s.value}</Text>
                    <Text className={styles.statLabel}>{s.label}</Text>
                    {s.trend && (
                      <span
                        className={styles.statTrend}
                        style={{ color: s.trend.direction === 'up' ? '#1A7F45' : '#C4314B' }}
                      >
                        <DataTrendingRegular />
                        {s.trend.pct}% vs last week
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Needs attention */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <Text className={styles.panelTitle}>Needs attention</Text>
                <Warning24Regular style={{ color: tokens.colorStatusDangerForeground1, fontSize: '18px' }} />
              </div>
              <div className={styles.alertList}>
                {alerts.map((a, i) => (
                  <div key={i} className={styles.alertItem} onClick={() => navigate(a.path)}>
                    <div className={styles.alertIconWrap} style={{ backgroundColor: a.iconBg }}>
                      {a.icon}
                    </div>
                    <div className={styles.alertText}>
                      <Text className={styles.alertTitle}>{a.title}</Text>
                      <Text className={styles.alertDesc}>{a.desc}</Text>
                    </div>
                    <ArrowRight16Regular style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column: sparkline + TBD */}
          <div className={styles.leftCol}>
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <Text className={styles.panelTitle}>Revenue — last 7 days</Text>
                <Button
                  appearance="transparent"
                  size="small"
                  icon={<ArrowRight16Regular />}
                  iconPosition="after"
                  onClick={() => navigate('/reports')}
                >
                  View reports
                </Button>
              </div>
              <div className={styles.sparklineBody}>
                <div className={styles.sparklineBars}>
                  {revenueByDay.map((d, i) => (
                    <div key={i} className={styles.sparklineCol}>
                      <div
                        className={styles.sparklineBar}
                        style={{
                          height: `${Math.round((d.value / maxRevenue) * 48)}px`,
                          backgroundColor: '#4F8EF7',
                          opacity: i === revenueByDay.length - 1 ? 0.4 : 1,
                        }}
                      />
                      <Text className={styles.sparklineDay}>{d.day}</Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
